import { memo, useCallback, useState } from 'react'
import type { FormEvent, KeyboardEvent } from 'react'
import type { Advertiser } from '../../store/slices/adsSlice.js'
import { advertisersApi } from '../../lib/adsApi.js'

interface AdvertisersTabProps {
  advertisers: Advertiser[]
  onUpdated: () => void
}

export const AdvertisersTab = memo(function AdvertisersTab({ advertisers, onUpdated }: AdvertisersTabProps) {
  const [newAdvName, setNewAdvName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [savingId, setSavingId] = useState<string | null>(null)

  const handleAdd = useCallback(async (e: FormEvent) => {
    e.preventDefault()
    if (!newAdvName.trim()) return
    setLoading(true)
    setError('')
    try {
      await advertisersApi.create({ name: newAdvName.trim() })
      setNewAdvName('')
      onUpdated()
    } catch (err: any) {
      setError(err.message ?? 'Erreur lors de la creation')
    } finally {
      setLoading(false)
    }
  }, [newAdvName, onUpdated])

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm('Voulez-vous vraiment supprimer cet annonceur ? Les campagnes associees seront conservees, mais l annonceur ne sera plus selectionnable.')) return
    try {
      await advertisersApi.delete(id)
      onUpdated()
    } catch (err: any) {
      alert(err.message ?? 'Erreur lors de la suppression')
    }
  }, [onUpdated])

  const handleStartEdit = useCallback((advertiser: Advertiser) => {
    setEditingId(advertiser.id)
    setEditingName(advertiser.name)
    setError('')
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingId(null)
    setEditingName('')
  }, [])

  const handleSaveEdit = useCallback(async (id: string) => {
    const name = editingName.trim()
    if (!name) {
      setError('Le nom de l annonceur est obligatoire')
      return
    }

    setSavingId(id)
    setError('')
    try {
      await advertisersApi.update(id, { name })
      setEditingId(null)
      setEditingName('')
      onUpdated()
    } catch (err: any) {
      setError(err.message ?? 'Erreur lors de la modification')
    } finally {
      setSavingId(null)
    }
  }, [editingName, onUpdated])

  return (
    <div className="advertisers-tab">
      <header className="advertisers-tab__header">
        <div>
          <h2 className="advertisers-tab__title">Annonceurs ({advertisers.length})</h2>
          <p className="advertisers-tab__description">
            Gérez la liste de vos annonceurs et clients pour la régie publicitaire.
          </p>
        </div>
      </header>

      <div className="advertisers-layout">
        <section className="advertisers-panel">
          <header className="advertisers-panel__header">
            <h3 className="advertisers-panel__title">Liste des annonceurs</h3>
          </header>

          <div className="advertisers-panel__list">
            {advertisers.length === 0 ? (
              <div className="tp-empty-state advertisers-panel__empty">Aucun annonceur enregistré</div>
            ) : (
              advertisers.map((adv, idx) => (
                <AdvertiserRow
                  key={adv.id}
                  advertiser={adv}
                  isLast={idx === advertisers.length - 1}
                  isEditing={editingId === adv.id}
                  editingName={editingName}
                  saving={savingId === adv.id}
                  onEditName={setEditingName}
                  onStartEdit={handleStartEdit}
                  onCancelEdit={handleCancelEdit}
                  onSaveEdit={handleSaveEdit}
                  onDelete={handleDelete}
                />
              ))
            )}
          </div>
        </section>

        <form onSubmit={handleAdd} className="advertiser-create-form">
          <h3 className="advertiser-create-form__title">Ajouter un annonceur</h3>

          <label className="tp-form-field">
            <span className="tp-field-label">Nom de l'annonceur</span>
            <input
              type="text"
              required
              value={newAdvName}
              onChange={e => setNewAdvName(e.target.value)}
              placeholder="Ex: McDonald's"
              className="tp-input"
            />
          </label>

          {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}

          <button
            type="submit"
            disabled={loading || !newAdvName.trim()}
            className="tp-button tp-button--primary advertiser-create-form__submit"
            data-state={loading ? 'loading' : undefined}
          >
            {loading ? 'Création...' : 'Ajouter'}
          </button>
        </form>
      </div>
    </div>
  )
})

interface AdvertiserRowProps {
  advertiser: Advertiser
  isLast: boolean
  isEditing: boolean
  editingName: string
  saving: boolean
  onEditName: (value: string) => void
  onStartEdit: (advertiser: Advertiser) => void
  onCancelEdit: () => void
  onSaveEdit: (id: string) => void
  onDelete: (id: string) => void
}

const AdvertiserRow = memo(function AdvertiserRow({
  advertiser,
  isLast,
  isEditing,
  editingName,
  saving,
  onEditName,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}: AdvertiserRowProps) {
  const displayName = isEditing ? editingName : advertiser.name

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onSaveEdit(advertiser.id)
    if (e.key === 'Escape') onCancelEdit()
  }, [advertiser.id, onCancelEdit, onSaveEdit])

  return (
    <div className={isLast ? 'advertiser-row is-last' : 'advertiser-row'}>
      <div className="advertiser-row__identity">
        <div className="advertiser-row__avatar" aria-hidden="true">
          {displayName.trim().charAt(0).toUpperCase() || '?'}
        </div>
        <div className="advertiser-row__copy">
          {isEditing ? (
            <input
              type="text"
              value={editingName}
              onChange={e => onEditName(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              className="tp-input advertiser-row__input"
            />
          ) : (
            <div className="advertiser-row__name">{advertiser.name}</div>
          )}
          {advertiser.contactEmail && !isEditing && <div className="advertiser-row__email">{advertiser.contactEmail}</div>}
        </div>
      </div>

      <div className="advertiser-row__actions">
        {isEditing ? (
          <>
            <button
              type="button"
              onClick={() => onSaveEdit(advertiser.id)}
              disabled={saving || !editingName.trim()}
              className="tp-button tp-button--primary advertiser-row__button"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button type="button" onClick={onCancelEdit} disabled={saving} className="tp-button advertiser-row__button">
              Annuler
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => onStartEdit(advertiser)} className="tp-button advertiser-row__button">
              Modifier
            </button>
            <button type="button" onClick={() => onDelete(advertiser.id)} className="tp-button tp-button--danger-outline advertiser-row__button">
              Supprimer
            </button>
          </>
        )}
      </div>
    </div>
  )
})
